/* eslint-disable no-param-reassign */
import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import getClients, {
	GetClientsParams,
	GetClientsResponse,
} from 'api/clients/getClients'
// import WabaManagement from 'api/clients/wabaManagement'

import { ClientProfile } from 'types'
import { ClientFieldFilter } from 'data/clientFilterFields'

export const CLIENT_DEFAULT_PAGE_SIZE = 10
/**
 * Types for the slice of client in the store
 */
export interface ClientState {
	records: ClientProfile[]
	totalCount: number
	totalPages: number
	currentPage: number
	loading: boolean
	active: boolean | undefined
	query: string
	suspended: boolean | undefined
	onBoarding: boolean | undefined
	onBoardingStep: number | undefined
	plan: 'free' | 'paid' | undefined
	platformMode: 'live' | 'demo' | undefined
	archivedView: boolean
	fieldFilters: ClientFieldFilter[]
	size: number
	// wabaManagementDetails?: WabaManagementApiResponse
}

/**
 * Initial state of the slice client
 */
const initialState: ClientState = {
	records: [],
	totalCount: 0,
	totalPages: 0,
	currentPage: 1,
	loading: false,
	active: undefined,
	query: '',
	suspended: undefined,
	onBoarding: undefined,
	onBoardingStep: undefined,
	plan: undefined,
	size: CLIENT_DEFAULT_PAGE_SIZE,
	platformMode: undefined,
	archivedView: false,
	fieldFilters: [],
}

/**
 * Action for getting clients
 *
 * Tries to get clients from the API and returns the result in the payload
 *
 * @param {GetClientsParams} options - Options for the API call
 * @returns {Promise<GetClientsResponse>} - Response object returned from the server
 * @throws {Error} - Throws an error if the API call fails
 */

export const getClientsAction = createAsyncThunk<
	GetClientsResponse,
	GetClientsParams
>('client/getClientsAction', async (options, { rejectWithValue }) => {
	try {
		const response = await getClients(options)
		return response
	} catch (error: any) {
		return rejectWithValue(error.message)
	}
})

export const clientSlice = createSlice({
	name: 'client',
	initialState,
	reducers: {
		clearClient: () => {
			return initialState
		},
		setClientQuery: (state, action: PayloadAction<string>) => {
			state.query = action.payload
		},
		setClientActive: (state, action: PayloadAction<boolean | undefined>) => {
			state.active = action.payload
		},
		setClientSuspended: (state, action: PayloadAction<boolean | undefined>) => {
			state.suspended = action.payload
		},
		setClientInOnboarding: (
			state,
			action: PayloadAction<boolean | undefined>
		) => {
			state.onBoarding = action.payload
		},
		setClientOnBoardingStep: (
			state,
			action: PayloadAction<number | undefined>
		) => {
			state.onBoardingStep = action.payload
		},
		setClientPlan: (
			state,
			action: PayloadAction<'free' | 'paid' | undefined>
		) => {
			state.plan = action.payload
		},
		setClientPlatformMode: (
			state,
			action: PayloadAction<'live' | 'demo' | undefined>
		) => {
			state.platformMode = action.payload
		},
		setClientArchivedView: (state, action: PayloadAction<boolean>) => {
			state.archivedView = action.payload
		},
		setClientFieldFilters: (
			state,
			action: PayloadAction<ClientFieldFilter[]>
		) => {
			state.fieldFilters = action.payload
		},
		clearClientFilters: state => {
			state.active = initialState.active
			state.query = initialState.query
			state.suspended = initialState.suspended
			state.onBoarding = initialState.onBoarding
			state.onBoardingStep = initialState.onBoardingStep
			state.plan = initialState.plan
			state.platformMode = initialState.platformMode
			state.fieldFilters = initialState.fieldFilters
		},
		setClientPageSize: (state, action: PayloadAction<number>) => {
			state.size = action.payload
		},
	},
	extraReducers: builder => {
		builder.addCase(getClientsAction.pending, state => {
			state.loading = true
		})
		builder.addCase(getClientsAction.fulfilled, (state, action) => {
			state.records = action.payload.records
			state.totalCount = action.payload.totalCount
			state.totalPages = action.payload.totalPages
			state.currentPage = action.payload.currentPage
			state.loading = false
		})
		builder.addCase(getClientsAction.rejected, state => {
			state.loading = false
		})
	},
})

export const {
	clearClient,
	setClientQuery,
	setClientActive,
	clearClientFilters,
	setClientInOnboarding,
	setClientOnBoardingStep,
	setClientSuspended,
	setClientPageSize,
	setClientPlan,
	setClientPlatformMode,
	setClientArchivedView,
	setClientFieldFilters,
} = clientSlice.actions

export default clientSlice.reducer
